import { useQuery } from '@tanstack/react-query'
import { QueryKey } from 'src/reactQuery/enums/queryKey.enum'
import { analyticsApi } from 'src/services/analyticsApi'
import { ReviewAnalysisApiService } from 'src/services/reviewAnalysisApi'
import { CalendarContext } from 'src/utils/calendarContext'

interface CalendarDataDatesResponse {
  dates?: string[]
  defaultRange?: string
  firstDate?: string
  lastDate?: string
}

interface CalendarDataDatesParams extends CalendarContext {
  month?: string
}

export type CalendarDotsRequest =
  | {
      facilityId?: string
      kind: 'review-analysis'
      month: string
      platformId?: string
      reviewCampaignId: string
    }
  | { kind: 'analytics'; params: Record<string, string> }
  | { kind: 'none' }

/**
 * Pure mapping from the calendar context to the request it makes — kept out of
 * the hook so it can be tested. Each id is only sent to the module it belongs
 * to, so a stray id can never narrow another module's dots.
 */
export const calendarDotsRequest = ({
  analysisId,
  campaignId,
  facilityId,
  keyword,
  month,
  pageId,
  platformId,
  source,
}: CalendarDataDatesParams): CalendarDotsRequest => {
  if (!month) return { kind: 'none' }

  // One review campaign (optionally one facility / platform of it) has its
  // own endpoint; without a campaign (the review listing) the analytics
  // endpoint spans every review campaign of the user.
  if (source === 'review-analysis' && analysisId) {
    return {
      facilityId: facilityId || undefined,
      kind: 'review-analysis',
      month,
      platformId: platformId || undefined,
      reviewCampaignId: analysisId,
    }
  }
  const params: Record<string, string> = { month }
  if (source) params.source = source
  if (analysisId) params.analysisId = analysisId
  if (pageId) params.pageId = pageId
  // The Social Listening branch reads ?campaign= / ?keyword= (parseFilter);
  // without them the dots are every campaign / keyword of the user.
  const isSocialListening = source === 'social-listening' || !source
  if (isSocialListening && campaignId) params.campaign = campaignId
  if (isSocialListening && keyword) params.keyword = keyword
  return { kind: 'analytics', params }
}

export const useGetCalendarDataDatesQuery = (
  params: CalendarDataDatesParams
) => {
  const {
    analysisId,
    campaignId,
    facilityId,
    keyword,
    month,
    pageId,
    platformId,
    source,
  } = params

  return useQuery({
    enabled: !!month,
    queryFn: async (): Promise<string[]> => {
      const request = calendarDotsRequest(params)
      if (request.kind === 'none') return []
      if (request.kind === 'review-analysis') {
        return ReviewAnalysisApiService.getCalendarDates(
          request.reviewCampaignId,
          request.month,
          { facilityId: request.facilityId, platformId: request.platformId }
        )
      }
      const response = await analyticsApi().get<CalendarDataDatesResponse>(
        '/v1_private/analytics/fetchCustomCalendarRange',
        { params: request.params }
      )
      return response.data.dates ?? []
    },
    queryKey: [
      QueryKey.CUSTOM_CALENDAR_DATA_DATES,
      month,
      source,
      pageId,
      analysisId,
      campaignId,
      facilityId,
      platformId,
      keyword,
    ],
  })
}
